import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { KineticLines, Reveal, Eyebrow } from "@/components/site/Reveal";
import { LeadDrawer } from "@/components/site/LeadDrawer";
import { SOLUTIONS, INDUSTRIES } from "@/data/content";

const scrollToId = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -110, duration: 1.2 });
};

export default function Solutions() {
  useEffect(() => {
    document.title = "B2B Lead Generation & Demand Generation Solutions | Vyacomm Media";
  }, []);

  return (
    <div data-testid="solutions-page">
      <section className="grain hero-grid-lines relative overflow-hidden bg-navy pb-20 pt-[200px]" data-testid="solutions-hero">
        <div className="pointer-events-none absolute -left-40 top-24 h-[420px] w-[420px] rounded-full bg-cobalt/20 blur-[140px]" />
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal delay={0.05} y={16}>
            <Eyebrow dark>Solutions Overview</Eyebrow>
          </Reveal>
          <h1 className="mt-6 max-w-4xl font-jakarta text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <KineticLines lines={["Full-Funnel Lead Generation,", "Engineered End to End."]} accentIndex={1} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Content syndication, MQL/HQL/BANT qualification, ABM activation, technographic targeting, webinar
              audiences, and verified B2B data — one accountable partner for your entire pipeline.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-9 flex flex-wrap gap-2.5">
              {SOLUTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={scrollToId(s.id)}
                  data-testid={`solutions-filter-${s.id}`}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-200 transition-all duration-300 hover:border-cobalt hover:bg-cobalt hover:text-white"
                >
                  {s.title}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {SOLUTIONS.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`py-20 sm:py-28 ${i % 2 === 0 ? "bg-canvas" : "bg-white"}`}
          data-testid={`solution-section-${s.id}`}
        >
          <div className="mx-auto grid max-w-[1808px] gap-12 px-5 sm:px-8 lg:px-12 2xl:px-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-4">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-cobaltlight">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <span className="font-jakarta text-5xl font-extrabold text-line">{s.num}</span>
                </div>
                <p className="mt-6 font-mono2 text-[10px] tracking-[0.24em] text-flame">{s.tag.toUpperCase()}</p>
                <h2 className="mt-3 font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                  {s.headline}
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-600">{s.desc}</p>
                <div className="mt-7 flex flex-wrap gap-2">
                  {s.specs.map((sp) => (
                    <span key={sp} className="rounded-full border border-line bg-white px-3.5 py-1.5 font-mono2 text-[10px] tracking-[0.12em] text-slate-500">
                      {sp.toUpperCase()}
                    </span>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/solutions/${s.id}`}
                    data-testid={`solution-detail-link-${s.id}`}
                    className="group inline-flex items-center gap-2 rounded-full bg-cobalt px-6 py-3 text-sm font-bold text-white transition-all duration-300 hover:scale-[1.02] hover:bg-[#1d4fd7]"
                  >
                    Explore Full Service Page
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/contact-us"
                    data-testid={`solution-cta-${s.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-cobalt px-6 py-3 text-sm font-bold text-cobalt transition-all duration-300 hover:bg-cobalt hover:text-white"
                  >
                    Scope This Solution
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-4 sm:grid-cols-2">
                {s.bullets.map((b, j) => (
                  <Reveal key={b.t} delay={0.1 + j * 0.07}>
                    <div className="h-full rounded-2xl border border-line bg-white p-6 shadow-[0_10px_30px_-20px_rgba(11,25,44,0.2)] transition-all duration-300 hover:-translate-y-1 hover:border-cobalt/50">
                      <CheckCircle2 className="h-5 w-5 text-cobalt" />
                      <h3 className="mt-3 font-jakarta text-base font-bold text-navy">{b.t}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* INDUSTRIES */}
      <section id="industries" className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="industries-section">
        <div className="pointer-events-none absolute -right-32 top-0 h-[380px] w-[380px] rounded-full bg-cobalt/15 blur-[130px]" />
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow dark>Industries & Audience Reach</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Specialized demand generation across enterprise technology verticals
            </h2>
            <Link
              to="/industries"
              data-testid="industries-page-link"
              className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-cobaltlight transition-colors hover:text-white"
            >
              Explore the full Industries page
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={i * 0.07}>
                <div
                  className="group h-full rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-flame/60"
                  data-testid={`industry-card-${i}`}
                >
                  <span className="font-mono2 text-xs tracking-[0.2em] text-flame">0{i + 1}</span>
                  <h3 className="mt-3 font-jakarta text-lg font-bold text-white">{ind.name}</h3>
                  <p className="mt-3 text-xs leading-relaxed text-slate-400">
                    <span className="font-mono2 text-[9px] tracking-[0.2em] text-cobaltlight">KEY PERSONAS — </span>
                    {ind.personas.join(" · ")}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl border border-white/10 bg-white/[0.04] p-8 sm:flex-row sm:items-center">
              <p className="max-w-xl font-jakarta text-xl font-bold text-white">
                Want to see exactly what a verified Vyacomm lead looks like before you commit?
              </p>
              <LeadDrawer
                buttonLabel="Inspect Sample Lead Record"
                buttonClass="inline-flex shrink-0 items-center gap-2 rounded-full border border-flame px-6 py-3 text-sm font-bold text-flame transition-all duration-300 hover:bg-flame hover:text-navy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
